export interface ScopeDefinition {
  id: string;
  label: string;
  category: string;
}

export const SCOPE_CATEGORIES = {
  voip: 'VoIP',
  interni: 'Interni',
  trunk: 'Trunk',
  negozi: 'Negozi',
  chiamate: 'Chiamate',
  registrazioni: 'Registrazioni',
  cdr: 'CDR',
  ai: 'AI',
  crm: 'CRM',
  campagne: 'Campagne',
  messaggistica: 'Messaggistica',
  pos: 'POS',
  calendario: 'Calendario',
} as const;

export const ALL_AVAILABLE_SCOPES: ScopeDefinition[] = [
  { id: 'voip:read', label: 'Lettura dati VoIP (Trunk, Interni, Stato)', category: 'voip' },
  { id: 'voip:write', label: 'Creazione/Modifica/Eliminazione Trunk', category: 'voip' },
  { id: 'extensions:read', label: 'Lettura Stato Interni (Registrazione, Stato)', category: 'interni' },
  { id: 'extensions:write', label: 'Creazione/Modifica/Eliminazione Interni', category: 'interni' },
  { id: 'extensions:sync', label: 'Sincronizzazione Interni (Bidirezionale)', category: 'interni' },
  { id: 'trunks:sync', label: 'Sincronizzazione Trunk (Bidirezionale)', category: 'trunk' },
  { id: 'stores:read', label: 'Lettura Negozi (SOLA LETTURA)', category: 'negozi' },
  { id: 'stores:sync', label: 'Sincronizzazione Negozi da Sistema Esterno', category: 'negozi' },
  { id: 'calls:control', label: 'Controllo Chiamate (Origina, Riaggancia, Trasferisci)', category: 'chiamate' },
  { id: 'recordings:access', label: 'Accesso Registrazioni (Lista, Download)', category: 'registrazioni' },
  { id: 'cdr:export', label: 'Esportazione CDR (Lista, Export CSV/JSON)', category: 'cdr' },
  { id: 'ai:config', label: 'Configurazione Agenti AI sui Trunk', category: 'ai' },
  { id: 'crm:read', label: 'Lettura dati CRM', category: 'crm' },
  { id: 'crm:write', label: 'Scrittura dati CRM', category: 'crm' },
  { id: 'campaigns:read', label: 'Lettura Campagne', category: 'campagne' },
  { id: 'campaigns:write', label: 'Gestione Campagne', category: 'campagne' },
  { id: 'messenger:read', label: 'Lettura Messaggi', category: 'messaggistica' },
  { id: 'messenger:write', label: 'Invio Messaggi', category: 'messaggistica' },
  { id: 'pos:read', label: 'Lettura dati POS', category: 'pos' },
  { id: 'pos:write', label: 'Gestione POS', category: 'pos' },
  {
    id: 'pos:purchase-orders:import',
    label: 'Import ordini fornitore (solo import, tenant-wide, Secret API key)',
    category: 'pos',
  },
  { id: 'calendar:read', label: 'Lettura Calendario (servizi, consulenti, disponibilità, appuntamenti)', category: 'calendario' },
  { id: 'calendar:write', label: 'Gestione Calendario (crea/aggiorna/cancella appuntamenti, submit form pubblici)', category: 'calendario' },
  // Task #1419 — scope opt-in per forzare i controlli di eleggibilità del
  // calendario (force_outside_shift / force_without_clockin /
  // force_missing_skills) via API S2S. Le chiavi con scope `*` continuano
  // a funzionare. Le chiavi con solo `calendar:write` ricevono 403 sui
  // force-flag.
  { id: 'calendar:override', label: 'Override controlli Calendario (force outside shift / without clockin / missing skills, richiede override_reason)', category: 'calendario' },
];

export const SCOPE_TO_EVENTS_MAP: Record<string, string[]> = {
  'voip:read': [
    'call.start', 'call.ringing', 'call.answered', 'call.hold', 'call.unhold', 'call.transfer', 'call.ended', 'call.recording_ready', 'call.dtmf',
    'call.transcript', 'agent.tool_call',
    'trunk.status', 'trunk.registered', 'trunk.unregistered',
    'extension.registered', 'extension.unregistered', 'extension.status',
    'queue.call.enter', 'queue.call.answered', 'queue.call.abandoned', 'queue.call.timeout',
    'queue.agent.login', 'queue.agent.logout', 'queue.agent.pause', 'queue.agent.unpause',
    'voicemail.new', 'voicemail.read', 'voicemail.deleted',
    'recording.ready', 'recording.deleted',
    'system.health',
  ],
  'voip:write': [
    'trunk.created', 'trunk.updated', 'trunk.deleted',
    'queue.created', 'queue.updated', 'queue.deleted',
    'ring_group.created', 'ring_group.updated', 'ring_group.deleted',
    'inbound_route.created', 'inbound_route.updated', 'inbound_route.deleted',
    'outbound_route.created', 'outbound_route.updated', 'outbound_route.deleted',
  ],
  'extensions:read': ['extension.registered', 'extension.unregistered', 'extension.status'],
  'extensions:write': ['extension.created', 'extension.updated', 'extension.deleted'],
  'extensions:sync': ['sync.completed', 'sync.failed'],
  'trunks:sync': ['sync.completed', 'sync.failed'],
  'stores:read': ['did.assigned', 'did.unassigned'],
  'stores:sync': ['sync.completed', 'sync.failed'],
  'calls:control': [
    'call.start', 'call.answered', 'call.ended', 'call.transfer', 'call.hold', 'call.unhold',
  ],
  'recordings:access': [
    'recording.ready', 'recording.deleted', 'call.recording_ready',
  ],
  'cdr:export': [
    'call.ended',
  ],
  'ai:config': ['system.health', 'api.error', 'agent.tool_call'],
  'crm:read': [],
  'crm:write': [],
  'campaigns:read': [],
  'campaigns:write': [],
  'messenger:read': [],
  'messenger:write': [],
  'pos:read': [],
  'pos:write': [],
  'calendar:read': [],
  'calendar:write': [],
  'calendar:override': [],
};

export const WEBHOOK_EVENT_CATEGORIES = [
  {
    name: 'Chiamate',
    events: ['call.start', 'call.ringing', 'call.answered', 'call.hold', 'call.unhold', 'call.transfer', 'call.ended', 'call.recording_ready', 'call.dtmf'],
  },
  {
    name: 'Trunk',
    events: ['trunk.created', 'trunk.updated', 'trunk.deleted', 'trunk.status', 'trunk.registered', 'trunk.unregistered'],
  },
  {
    name: 'Interni',
    events: ['extension.created', 'extension.updated', 'extension.deleted', 'extension.registered', 'extension.unregistered', 'extension.status'],
  },
  {
    name: 'Code',
    events: ['queue.created', 'queue.updated', 'queue.deleted', 'queue.agent.login', 'queue.agent.logout', 'queue.agent.pause', 'queue.agent.unpause', 'queue.call.enter', 'queue.call.answered', 'queue.call.abandoned', 'queue.call.timeout'],
  },
  {
    name: 'Gruppi di Squillo',
    events: ['ring_group.created', 'ring_group.updated', 'ring_group.deleted'],
  },
  {
    name: 'Instradamento',
    events: ['inbound_route.created', 'inbound_route.updated', 'inbound_route.deleted', 'outbound_route.created', 'outbound_route.updated', 'outbound_route.deleted'],
  },
  {
    name: 'Segreteria',
    events: ['voicemail.new', 'voicemail.read', 'voicemail.deleted'],
  },
  {
    name: 'Registrazioni',
    events: ['recording.ready', 'recording.deleted'],
  },
  {
    name: 'DID',
    events: ['did.assigned', 'did.unassigned'],
  },
  {
    name: 'Sistema',
    events: ['system.health', 'api.error', 'sync.completed', 'sync.failed'],
  },
  {
    name: 'AI',
    events: ['call.transcript', 'agent.tool_call'],
  },
];

export function getAllowedEventsForScopes(scopes: string[]): Set<string> {
  const allowed = new Set<string>();
  for (const scope of scopes) {
    const events = SCOPE_TO_EVENTS_MAP[scope];
    if (events) events.forEach(e => allowed.add(e));
  }
  return allowed;
}

export function getScopesForEvent(event: string): string[] {
  const scopes: string[] = [];
  for (const [scope, events] of Object.entries(SCOPE_TO_EVENTS_MAP)) {
    if (events.includes(event)) {
      scopes.push(scope);
    }
  }
  return scopes;
}
